import { omit, mapValues, keys } from 'remeda'
import { paramCase } from 'change-case'

/* eslint-disable @typescript-eslint/no-explicit-any */
type PathInputConfig = {
  value?: string
  param?: boolean
}

type SubPaths<T> = {
  [key in keyof T]: PathInput<T[key]>
}

type PathInput<T> = PathInputConfig & SubPaths<Omit<T, keyof PathInputConfig>>

type PathOutputInfo<Params> = {
  path: string
  getPath: (p: Params) => string
}

type GetParameters<T, key extends keyof T> = Partial<Record<key, string>>

type PathsOutput<T, Params> = {
  [key in keyof Omit<T, keyof PathInputConfig>]: PathsOutput<
    T[key],
    Params & GetParameters<SubPaths<T>, key>
  > &
    PathOutputInfo<Params & GetParameters<SubPaths<T>, key>>
}

const keysOfConfigMap: Record<keyof PathInputConfig, true> = {
  value: true,
  param: true,
}
const keysOfConfig = keys(keysOfConfigMap)

export const buildPaths = <T extends PathInput<T>>(
  rootPaths: T,
  // Using Record<never, unknown | never> causes typing issues for getPath
  // eslint-disable-next-line
): PathsOutput<T, {}> => {
  const impl = (
    paths: PathInput<any>,
    { from, prevGetPath }: { prevGetPath: (p: any) => string; from: string },
  ): PathsOutput<any, any> => {
    const getPath = (p: Record<string, unknown>) => {
      return `${prevGetPath(p)}${from === '' ? '' : '/'}${
        paths.param
          ? p[from]
          : (paths.value ??
            (from === 'v2'
              ? from
              : paramCase(from, {
                  splitRegexp: [
                    /([a-z0-9])([A-Z])/g,
                    /([A-Z])([A-Z][a-z])/g,
                    /([a-zA-Z])([0-9])/g,
                    /([0-9])([a-zA-Z])/g,
                  ],
                })))
      }`
    }

    return {
      ...(mapValues(omit(paths, keysOfConfig), (value, key) =>
        impl(value as PathInput<any>, {
          prevGetPath: getPath,
          from: key as unknown as string,
        }),
      ) as any),
      path: getPath({}),
      getPath,
    }
  }
  return impl(rootPaths, { from: '', prevGetPath: () => '' })
}
